import { useCallback, useEffect, useState } from "react";
import { PackageCheck, Truck, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// HQ management of studio roll returns: confirm receipt, redispatch, or write off (damaged).
export const RollReturnsHQ = () => {
  const [pending, setPending] = useState([]);
  const [studios, setStudios] = useState([]);
  const [redispatch, setRedispatch] = useState(null);
  const [writeoff, setWriteoff] = useState(null);
  const [form, setForm] = useState({ studio_id: "", lr_number: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/roll-serials/returns?status=pending").then(({ data }) => setPending(data || [])).catch(() => setPending([]));
  }, []);

  useEffect(() => {
    load();
    api.get("/studios").then(({ data }) => setStudios(data || [])).catch(() => {});
  }, [load]);

  const confirm = async (r) => {
    setBusy(true);
    try {
      await api.post(`/roll-serials/${r.id}/return/confirm`);
      toast.success(`${r.serial} received into HQ pool`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitRedispatch = async () => {
    setBusy(true);
    try {
      // roll must be confirmed into HQ pool first
      await api.post(`/roll-serials/${redispatch.id}/return/confirm`).catch(() => {});
      await api.post(`/roll-serials/${redispatch.id}/redispatch`, {
        studio_id: form.studio_id, lr_number: form.lr_number,
      });
      toast.success(`${redispatch.serial} redispatched`);
      setRedispatch(null); setForm({ studio_id: "", lr_number: "", reason: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitWriteoff = async () => {
    setBusy(true);
    try {
      await api.post(`/roll-serials/${writeoff.id}/writeoff`, { reason: form.reason });
      toast.success(`${writeoff.serial} written off`);
      setWriteoff(null); setForm({ studio_id: "", lr_number: "", reason: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!pending.length) return null;

  return (
    <Card className="mb-6 border-amber-200 shadow-none" data-testid="hq-roll-returns">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <PackageCheck className="h-4 w-4 text-amber-600" /> Roll returns awaiting HQ ({pending.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="p-2.5">Serial</th>
              <th className="p-2.5">Material</th>
              <th className="p-2.5">From studio</th>
              <th className="p-2.5 text-right">Remaining</th>
              <th className="p-2.5">Reason</th>
              <th className="p-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {pending.map((r) => (
              <tr key={r.id} className="border-t border-brand-line" data-testid={`hq-return-${r.serial}`}>
                <td className="p-2.5 font-mono">{r.serial}</td>
                <td className="p-2.5">{r.product_type}</td>
                <td className="p-2.5">{r.return_request?.from_studio_name || r.studio_name}</td>
                <td className="p-2.5 text-right font-medium">{Math.round(r.balance_sqft)} sqft</td>
                <td className="p-2.5 text-slate-500">{r.return_request?.reason}</td>
                <td className="p-2.5 text-right">
                  <div className="flex flex-wrap items-center justify-end gap-1.5">
                    <Button size="sm" variant="outline" className="h-8" disabled={busy}
                      data-testid={`hq-return-confirm-${r.serial}`} onClick={() => confirm(r)}>
                      <PackageCheck className="mr-1 h-3.5 w-3.5" /> Confirm
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" disabled={busy}
                      data-testid={`hq-return-redispatch-${r.serial}`}
                      onClick={() => { setRedispatch(r); setForm({ studio_id: "", lr_number: "", reason: "" }); }}>
                      <Truck className="mr-1 h-3.5 w-3.5" /> Redispatch
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50"
                      disabled={busy} data-testid={`hq-return-writeoff-${r.serial}`}
                      onClick={() => { setWriteoff(r); setForm({ studio_id: "", lr_number: "", reason: "" }); }}>
                      <Ban className="mr-1 h-3.5 w-3.5" /> Write-off
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={!!redispatch} onOpenChange={(o) => !o && setRedispatch(null)}>
        <DialogContent className="sm:max-w-md" data-testid="hq-redispatch-dialog">
          <DialogHeader><DialogTitle>Redispatch {redispatch?.serial}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-500">
            {Math.round(redispatch?.balance_sqft || 0)} sqft — same serial number, sent to a studio.
          </DialogDescription>
          <div>
            <Label>Send to studio *</Label>
            <Select value={form.studio_id} onValueChange={(v) => setForm((f) => ({ ...f, studio_id: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="hq-redispatch-studio"><SelectValue placeholder="Choose studio" /></SelectTrigger>
              <SelectContent>
                {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>LR number (optional)</Label>
            <Input className="mt-1.5" data-testid="hq-redispatch-lr" value={form.lr_number}
              onChange={(e) => setForm((f) => ({ ...f, lr_number: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedispatch(null)}>Cancel</Button>
            <Button data-testid="hq-redispatch-submit" disabled={busy || !form.studio_id} onClick={submitRedispatch}>
              Redispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!writeoff} onOpenChange={(o) => !o && setWriteoff(null)}>
        <DialogContent className="sm:max-w-md" data-testid="hq-writeoff-dialog">
          <DialogHeader><DialogTitle>Write off {writeoff?.serial}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-500">
            {Math.round(writeoff?.balance_sqft || 0)} sqft will be written off and removed from usable stock.
          </DialogDescription>
          <div>
            <Label>Reason (damaged / rejected) *</Label>
            <Input className="mt-1.5" data-testid="hq-writeoff-reason" value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriteoff(null)}>Cancel</Button>
            <Button data-testid="hq-writeoff-submit" className="bg-red-600 hover:bg-red-700"
              disabled={busy || !form.reason.trim()} onClick={submitWriteoff}>
              Write off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RollReturnsHQ;
